import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { websitePortfolioData } from "@/constants";
import { PortfolioDataItem } from "@/types/types";

gsap.registerPlugin(ScrollTrigger);

type GalleryProject = {
  id: string;
  name: string;
  industry: string;
  description: string;
  tech: string[];
  image: string;
  mobileImage: string;
  url: string;
  caseStudyUrl: string;
  palette: [string, string];
};

const snippets = [
  "responsive layouts",
  "seo-ready pages",
  "cms workflows",
  "fast landing pages",
  "analytics dashboards",
  "conversion funnels",
];

const splitText = (text: string) =>
  text.split(" ").map((word, wordIndex, words) => (
    <span
      key={`${word}-${wordIndex}`}
      className="inline-block whitespace-nowrap"
    >
      {word.split("").map((char, charIndex) => (
        <span
          key={`${char}-${wordIndex}-${charIndex}`}
          data-split-char
          aria-hidden="true"
          className="inline-block will-change-transform"
        >
          {char}
        </span>
      ))}
      {wordIndex < words.length - 1 && <span aria-hidden="true"> </span>}
    </span>
  ));

const toGalleryProject = (
  project: PortfolioDataItem,
  index: number,
): GalleryProject => ({
  id: String(index + 1),
  name: project.brand,
  industry: project.title,
  description: project.description,
  tech: project.techStack || [],
  image: project.media[0] || project.mobileMedia?.[0] || "",
  mobileImage: project.mobileMedia?.[0] || project.media[0] || "",
  url: project.visitUrl || "#",
  caseStudyUrl: project.caseStudyUrl || "#",
  palette: [project.accentFrom || "#c3f620", project.accentTo || "#00a878"],
});

const BrowserMock = ({ project }: { project: GalleryProject }) => (
  <div className="relative flex aspect-[16/10] min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-[#00a878]/30 bg-[#0d1821]/95 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.78),0_0_70px_rgba(0,168,120,0.22),inset_0_1px_0_rgba(195,246,32,0.14)] backdrop-blur-xl">
    <div className="flex shrink-0 items-center gap-2 border-b border-[#00a878]/20 bg-[#07131b]/95 px-4 py-3 backdrop-blur-md">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
      <div className="ml-4 flex h-6 flex-1 items-center justify-center rounded-md bg-[#f5f5f5]/8 px-3 font-mono text-[10px] uppercase tracking-widest text-[#c3f620]/80">
        {project.id}.adszie
      </div>
    </div>

    <div className="relative min-h-0 flex-1 overflow-hidden bg-[#02070a]">
      <img
        src={project.image}
        alt={`${project.name} website preview`}
        width={1280}
        height={1600}
        loading="eager"
        className="absolute inset-x-0 top-0 min-h-full w-full object-cover"
        data-screenshot
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(195,246,32,0.08), transparent 30%), radial-gradient(120% 60% at 50% 0%, rgba(0,168,120,0.18), transparent 60%)",
        }}
      />
      <div
        data-reflection
        className="pointer-events-none absolute -inset-px opacity-50 mix-blend-screen"
        style={{
          background:
            "radial-gradient(400px 200px at var(--mx,30%) var(--my,20%), rgba(195,246,32,0.24), transparent 60%)",
        }}
      />
    </div>
  </div>
);

const MobileWebsiteProjects = ({
  projects,
}: {
  projects: GalleryProject[];
}) => (
  <section className="relative overflow-hidden bg-[#0d1821] px-5 pb-12 pt-28 text-[#f5f5f5] md:hidden">
    <div className="absolute inset-0 web-gallery-grid opacity-50" />
    <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-[#00a878]/25 blur-[80px]" />
    <div className="pointer-events-none absolute -right-40 top-[32rem] h-96 w-96 rounded-full bg-[#c3f620]/18 blur-[90px]" />
    <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-[#07131b] to-transparent" />

    <div className="relative z-10 mx-auto max-w-xl">
      {/* <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#c3f620]">
        Adszie / Web Services
      </p> */}
      <h1 className="mt-5 font-orbitron text-4xl font-medium uppercase leading-tight">
        <span className="text-[#c3f620]">We Build</span>
        <br />
        Web Experiences
      </h1>
      <p className="mt-5 font-poppins text-base leading-7 text-[#f5f5f5]/72">
        Modern websites designed for performance, growth, and memorable user
        experiences.
      </p>

      <div className="mt-10 space-y-8">
        {projects.map((project, index) => (
          <article
            key={project.id}
            className="rounded-lg border border-[#f5f5f5]/10 bg-[#f5f5f5]/5 p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#00a878]">
                  {String(index + 1).padStart(2, "0")} /{" "}
                  {String(projects.length).padStart(2, "0")}
                </p>
                <h2 className="mt-3 font-orbitron text-2xl uppercase tracking-[1px]">
                  {project.name}
                </h2>
                <p className="mt-1 font-poppins text-xs lg:text-sm uppercase tracking-[0.16em] text-[#c3f620]">
                  {project.industry}
                </p>
              </div>
            </div>

            <p className="mt-4 font-poppins text-sm leading-6 text-[#f5f5f5]/70">
              {project.description}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {project.tech.map((tech) => (
                <span
                  key={tech}
                  className="rounded-full border border-[#00a878]/30 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#f5f5f5]/80"
                >
                  {tech}
                </span>
              ))}
            </div>

            <div className="mt-6">
              <div className="overflow-hidden rounded-lg border border-[#00a878]/25 bg-black shadow-2xl">
                <img
                  // src={project.mobileImage}
                  src={project.image}
                  alt={`${project.name} mobile website screenshot`}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full object-contain"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={project.url}
                target={project.url === "#" ? undefined : "_blank"}
                rel={project.url === "#" ? undefined : "noreferrer"}
                onClick={(event) => {
                  if (project.url === "#") event.preventDefault();
                }}
                className="inline-flex items-center gap-2 rounded-full bg-[#c3f620] px-5 py-2.5 font-poppins text-sm font-semibold text-[#0d1821]"
              >
                Visit website
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const WebServicesPromo = () => {
  const rootRef = useRef<HTMLElement | null>(null);
  const headlineRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const orbARef = useRef<HTMLDivElement | null>(null);
  const orbBRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const projects = useMemo(
    () => (websitePortfolioData as PortfolioDataItem[]).map(toGalleryProject),
    [],
  );
  const active = projects[activeIndex] || projects[0];

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || projects.length === 0) return;

    const media = gsap.matchMedia();

    media.add("(min-width: 768px)", () => {
      let frame = 0;
      let ctx: gsap.Context | null = null;

      frame = window.requestAnimationFrame(() => {
        if (!root.isConnected) return;

        const cards = cardsRef.current.filter(Boolean) as HTMLDivElement[];
        ctx = gsap.context(() => {
          gsap.ticker.lagSmoothing(1000, 16);
          ScrollTrigger.config({ ignoreMobileResize: true });

          const chars =
            headlineRef.current?.querySelectorAll<HTMLElement>(
              "[data-split-char]",
            ) ?? [];

          gsap.set(chars, { yPercent: 110, opacity: 0, rotateX: -60 });
          gsap.to(chars, {
            yPercent: 0,
            opacity: 1,
            rotateX: 0,
            ease: "expo.out",
            duration: 1.1,
            stagger: 0.018,
            scrollTrigger: {
              trigger: root,
              start: "top 75%",
              once: true,
            },
          });

          cards.forEach((card, index) => {
            gsap.set(card, {
              opacity: index === 0 ? 1 : 0,
              scale: index === 0 ? 1 : 0.2,
              rotateY: index === 0 ? 0 : -25,
              z: index === 0 ? 0 : -1200,
              filter: index === 0 ? "blur(0px)" : "blur(20px)",
              transformPerspective: 1400,
              transformOrigin: "50% 50%",
            });
          });

          const timeline = gsap.timeline({
            scrollTrigger: {
              trigger: root,
              start: "top top",
              end: () => `+=${window.innerHeight * projects.length * 0.9}`,
              pin: true,
              scrub: 1,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              onUpdate: (self) => {
                const nextIndex = Math.min(
                  projects.length - 1,
                  Math.floor(self.progress * projects.length),
                );
                setActiveIndex(nextIndex);
              },
            },
          });

          cards.forEach((card, index) => {
            const screenshot =
              card.querySelector<HTMLImageElement>("[data-screenshot]");

            if (index === 0 && screenshot) {
              timeline.fromTo(
                screenshot,
                { yPercent: 0 },
                { yPercent: -38, ease: "none", duration: 1 },
                0,
              );
              return;
            }

            const previous = cards[index - 1];
            if (!previous) return;

            timeline.to(
              previous,
              {
                opacity: 0,
                scale: 1.4,
                z: 600,
                rotateY: 25,
                filter: "blur(28px)",
                ease: "power2.in",
                duration: 1,
              },
              index,
            );

            timeline.fromTo(
              card,
              {
                opacity: 0,
                scale: 0.2,
                z: -1400,
                rotateY: -30,
                filter: "blur(24px)",
              },
              {
                opacity: 1,
                scale: 1,
                yPercent: 0,
                z: 0,
                rotateY: 0,
                filter: "blur(0px)",
                ease: "power2.out",
                duration: 1,
              },
              index,
            );

            if (screenshot) {
              timeline.fromTo(
                screenshot,
                { yPercent: 0 },
                { yPercent: -38, ease: "none", duration: 1 },
                index - 0.5,
              );
            }
          });

          gsap.to(orbARef.current, {
            x: 80,
            y: -40,
            duration: 12,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
          gsap.to(orbBRef.current, {
            x: -100,
            y: 60,
            duration: 16,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
        }, root);
      });

      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        ctx?.revert();
      };
    });

    return () => media.revert();
  }, [projects]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let frame = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;

    const handleMove = (event: MouseEvent) => {
      const rect = stage.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;

      targetX = (px - 0.5) * 16;
      targetY = (0.5 - py) * 10;

      const activeCard = cardsRef.current[activeIndex];
      const reflection =
        activeCard?.querySelector<HTMLDivElement>("[data-reflection]");

      if (reflection) {
        reflection.style.setProperty("--mx", `${px * 100}%`);
        reflection.style.setProperty("--my", `${py * 100}%`);
      }

      if (!frame) {
        frame = requestAnimationFrame(() => {
          currentX += (targetX - currentX) * 0.1;
          currentY += (targetY - currentY) * 0.1;
          stage.style.setProperty("--rx", `${currentY.toFixed(2)}deg`);
          stage.style.setProperty("--ry", `${currentX.toFixed(2)}deg`);
          frame = 0;
        });
      }
    };

    stage.addEventListener("mousemove", handleMove);

    return () => {
      stage.removeEventListener("mousemove", handleMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [activeIndex]);

  if (!active) return null;

  return (
    <>
      <MobileWebsiteProjects projects={projects} />
      <section
        ref={rootRef}
        className="relative hidden h-screen w-full overflow-hidden bg-[#0d1821] text-[#f5f5f5] md:block"
        aria-label="Adszie web development gallery"
      >
        <style>
          {`
            @keyframes webGalleryFloat {
              0%, 100% { transform: translate3d(0, 0, 0); }
              50% { transform: translate3d(10px, -20px, 0); }
            }

            @keyframes webGalleryPulse {
              0%, 100% { opacity: 0.45; }
              50% { opacity: 1; }
            }

            .web-gallery-grid {
              background-image:
                linear-gradient(to right, rgba(0, 168, 120, 0.11) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 168, 120, 0.11) 1px, transparent 1px);
              background-size: 64px 64px;
            }

            .web-gallery-noise {
              background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>");
            }
          `}
        </style>

        <div className="relative h-full overflow-hidden">
          <div className="absolute inset-0 web-gallery-grid opacity-60" />
          <div className="pointer-events-none absolute inset-0 web-gallery-noise opacity-[0.07] mix-blend-overlay" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0d1821] to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0d1821] to-transparent" />

          <div
            ref={orbARef}
            className="pointer-events-none absolute -left-32 top-1/4 h-[42rem] w-[42rem] rounded-full opacity-[0.28] blur-[90px] transition-colors duration-1000"
            style={{ background: active.palette[0] }}
          />
          <div
            ref={orbBRef}
            className="pointer-events-none absolute -right-32 bottom-0 h-[36rem] w-[36rem] rounded-full opacity-[0.24] blur-[90px] transition-colors duration-1000"
            style={{ background: active.palette[1] }}
          />

          <div className="pointer-events-none absolute inset-0 hidden overflow-hidden md:block">
            {snippets.map((snippet, index) => (
              <div
                key={snippet}
                className="absolute font-mono text-[11px] tracking-tight text-[#c3f620]/20"
                style={{
                  top: `${(index * 17 + 8) % 90}%`,
                  left: `${(index * 23 + 5) % 85}%`,
                  animation: "webGalleryFloat 9s ease-in-out infinite",
                  animationDelay: `${index * 0.7}s`,
                }}
              >
                {snippet}
              </div>
            ))}
          </div>

          <div className="relative z-10 grid h-full grid-cols-1 gap-6 px-6 py-10 md:gap-8 lg:grid-cols-[minmax(380px,0.8fr)_minmax(620px,1.5fr)] lg:items-center lg:px-20 lg:py-16 xl:px-24">
            <div className="flex flex-col justify-between">
              <div ref={headlineRef} className="max-w-[42rem]">
                {/* <div className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#f5f5f5]/55">
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-[#c3f620]"
                    style={{ animation: "webGalleryPulse 3s ease-in-out infinite" }}
                  />
                  Adszie / Web Services
                </div> */}
                <h2 className="text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-[clamp(3.4rem,4.45vw,5.5rem)]">
                  <span
                    aria-label="We Build"
                    className="bg-gradient-to-r from-[#c3f620] via-[#00a878] to-[#62e6c8] bg-clip-text text-[#c3f620] transition-colors duration-1000"
                  >
                    {splitText("We Build")}
                  </span>
                  <br />
                  <span aria-label="Web Experiences">
                    {splitText("Web Experiences")}
                  </span>
                </h2>
                <p className="mt-6 max-w-md font-poppins text-base text-[#f5f5f5]/68">
                  Modern websites designed for performance, growth, and
                  memorable user experiences.
                </p>
              </div>

              <div className="mt-8 max-w-md md:mt-12">
                <div className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#f5f5f5]/45">
                  <span>
                    {String(activeIndex + 1).padStart(2, "0")} /{" "}
                    {String(projects.length).padStart(2, "0")}
                  </span>
                  <span className="h-px flex-1 bg-[#00a878]/30" />
                  <span className="text-[#c3f620]/80">{active.industry}</span>
                </div>

                <div key={active.id} className="animate-fade-in">
                  <h3 className="text-3xl font-medium tracking-tight sm:text-4xl">
                    {active.name}
                  </h3>
                  <p className="mt-3 font-poppins text-sm leading-relaxed text-[#f5f5f5]/68">
                    {active.description}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {active.tech.map((tech) => (
                      <span
                        key={tech}
                        className="rounded-full border border-[#00a878]/25 bg-[#00a878]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#f5f5f5]/80 backdrop-blur"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={active.url}
                      target={active.url === "#" ? undefined : "_blank"}
                      rel={active.url === "#" ? undefined : "noreferrer"}
                      onClick={(event) => {
                        if (active.url === "#") event.preventDefault();
                      }}
                      className="group inline-flex items-center gap-2 rounded-full bg-[#c3f620] px-5 py-2.5 font-poppins text-sm font-semibold text-[#0d1821] transition-transform hover:scale-[1.03] hover:bg-[#f5f5f5]"
                    >
                      Visit website
                      <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                    {/* <a
                      href="#"
                      onClick={(event) => event.preventDefault()}
                      className="inline-flex items-center gap-2 rounded-full border border-[#00a878]/35 bg-[#0d1821]/40 px-5 py-2.5 font-poppins text-sm font-medium text-[#f5f5f5]/90 backdrop-blur transition-colors hover:bg-[#00a878]/15"
                    >
                      Case study
                    </a> */}
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={stageRef}
              className="relative flex min-h-[230px] items-center justify-center sm:min-h-[320px] lg:min-h-[540px] lg:justify-end"
              style={{ perspective: "1400px" }}
            >
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
                viewBox="0 0 600 600"
                fill="none"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="web-gallery-line" x1="0" x2="1">
                    <stop
                      offset="0"
                      stopColor={active.palette[0]}
                      stopOpacity="0"
                    />
                    <stop
                      offset="0.5"
                      stopColor={active.palette[1]}
                      stopOpacity="0.6"
                    />
                    <stop
                      offset="1"
                      stopColor={active.palette[0]}
                      stopOpacity="0"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M0 480 Q 300 300 600 520"
                  stroke="url(#web-gallery-line)"
                  strokeWidth="1"
                />
                <path
                  d="M0 120 Q 300 280 600 80"
                  stroke="url(#web-gallery-line)"
                  strokeWidth="1"
                />
              </svg>

              <div
                className="relative w-full max-w-[820px] xl:max-w-[900px]"
                style={{
                  transform: "rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))",
                  transformStyle: "preserve-3d",
                  transition: "transform 0.25s ease-out",
                }}
              >
                {projects.map((project, index) => (
                  <div
                    key={project.id}
                    ref={(element) => {
                      cardsRef.current[index] = element;
                    }}
                    className={`absolute inset-0 will-change-transform ${
                      index === 0 ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <BrowserMock project={project} />
                  </div>
                ))}
                <div className="invisible">
                  <BrowserMock project={projects[0]} />
                </div>
              </div>

              <div className="absolute -right-2 top-1/2 hidden -translate-y-1/2 flex-col gap-3 lg:flex">
                {projects.map((project, index) => (
                  <span
                    key={project.id}
                    className={`h-8 w-0.5 rounded-full transition-all duration-500 ${
                      index === activeIndex
                        ? "bg-[#c3f620]"
                        : index < activeIndex
                          ? "bg-[#00a878]/70"
                          : "bg-[#f5f5f5]/20"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#c3f620]/55 md:block">
            Scroll to explore the gallery
          </div>
        </div>
      </section>
    </>
  );
};

export default WebServicesPromo;
